/**
 * clientHistoryService (Jose 2026-09-10) — READER for the imported Fresha client history. The Fresha
 * import (client insights + sales log) writes a SHARDED roster (`clientRoster/{i}`, ~400 clients each)
 * + a walk-in log (`walkInLog/{barberId}`). This service loads + caches them and exposes the client
 * metrics the reports + the customer portal need. Writer = freshaClientMigration.ts.
 *
 * Design: sharded roster docs → the whole client base loads in ~4 reads (not 1,342), and the portal
 * can find one client by phone/email from the same load. BEHAVIOR-PIN: project-slik-client-linkage-gap-retention-2026-09-10
 */
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getOperatorDb, getOperatorBarberId, onOperatorChange } from './operatorSession';
import type { ClientRecord, WalkInRecord } from './freshaClientImport';

export type { ClientRecord, WalkInRecord } from './freshaClientImport';

export interface ClientHistoryMeta {
  totalClients: number; namedVisits: number; walkInVisits: number; namedPct: number;
  dateRange: { min: string; max: string } | null;
}

let _clientsCache: ClientRecord[] | null = null;
let _walkCache: WalkInRecord[] | null = null;
export function clearClientHistoryCache(): void { _clientsCache = null; _walkCache = null; }
// SECURITY (Phase B 2026-09-30): walkInLog is owner/manager/own-gated → read via the OPERATOR session;
// clear the walk-in cache on operator change. (clientRoster stays isStaff → main db, unchanged.)
onOperatorChange(() => { _walkCache = null; });

/** Load the full client roster (merged from all shards). Cached. Empty [] if nothing imported yet. */
export async function loadClientHistory(): Promise<ClientRecord[]> {
  if (_clientsCache) return _clientsCache;
  try {
    const snap = await getDocs(collection(db, 'clientRoster'));
    const out: ClientRecord[] = [];
    snap.forEach((d) => {
      if (d.id === '_meta') return;
      const data = d.data() as any;
      const clients = data.clients || {};
      for (const key of Object.keys(clients)) out.push(clients[key] as ClientRecord);
    });
    _clientsCache = out;
    return out;
  } catch (e) { console.warn('loadClientHistory failed', e); return []; }
}

/** Load the per-barber walk-in log (foot-traffic + hour-of-day). Cached. Read via the OPERATOR session
 *  (owner/manager → all; barber → own doc; nobody tapped in → empty). */
export async function loadWalkInLog(): Promise<WalkInRecord[]> {
  if (_walkCache) return _walkCache;
  // Operator session if tapped in, else the main login (owner reads all; bare shop account rule-denied).
  const rdb = getOperatorDb() || db;
  try {
    const out: WalkInRecord[] = [];
    const snap = await getDocs(collection(rdb, 'walkInLog'));
    snap.forEach((d) => { if (d.id !== '_meta') out.push(d.data() as WalkInRecord); });
    _walkCache = out;
    return out;
  } catch (e) {
    const bid = getOperatorBarberId();
    _walkCache = [];
    if (bid) { try { const d = await getDoc(doc(rdb, 'walkInLog', bid)); if (d.exists()) _walkCache = [d.data() as WalkInRecord]; } catch { /* own denied */ } }
    return _walkCache;
  }
}

/** SHOP-WIDE client metrics (the Client Growth slide's real numbers). */
export function shopClientMetrics(clients: ClientRecord[]) {
  const n = clients.length;
  if (!n) return { totalClients: 0, returning: 0, retentionPct: 0, rebookedPct: 0, avgVisits: 0, avgLtv: 0 };
  let returning = 0, rebooked = 0, visits = 0, spend = 0;
  for (const c of clients) {
    if ((c.totalVisits || 0) > 1) returning++;
    if (c.rebooked) rebooked++;
    visits += c.totalVisits || 0; spend += c.totalSpend || 0;
  }
  return {
    totalClients: n,
    returning,
    retentionPct: Math.round((returning / n) * 1000) / 10,
    rebookedPct: Math.round((rebooked / n) * 1000) / 10,
    avgVisits: Math.round((visits / n) * 10) / 10,
    avgLtv: Math.round(spend / n),
  };
}

/**
 * PER-BARBER retention over [start,end]: of the clients who visited THIS barber (by barberId) in the
 * window, what % are returning (>1 lifetime visit to this barber). barberDates is keyed by barberId
 * (remapped at import). Returns null when there's too little linked data to trust.
 */
export function barberRetentionById(
  clients: ClientRecord[],
  barberId: string,
  startISO: string,
  endISO: string,
): { retentionPct: number | null; seen: number; returning: number } {
  let seen = 0, returning = 0;
  for (const c of clients) {
    const dates = c.barberDates?.[barberId];
    if (!dates || !dates.length) continue;
    if (!dates.some((d) => d >= startISO && d <= endISO)) continue;
    seen++;
    if (dates.length > 1) returning++;
  }
  return { retentionPct: seen >= 5 ? Math.round((returning / seen) * 1000) / 10 : null, seen, returning };
}

/**
 * PER-BARBER rebooking from the imported client history (mature rolling window, mirrors the growth
 * engine's appointment-based calc but on the REAL client dates). Of clients whose visit to this barber
 * fell in [now-90d, now-30d], what % came back within 45 days? Returns null when too few anchors to trust.
 */
export function barberRebookingById(
  clients: ClientRecord[],
  barberId: string,
  nowMs: number,
): number | null {
  const matureStart = nowMs - 90 * 86400000, matureEnd = nowMs - 30 * 86400000;
  let denom = 0, yes = 0;
  for (const c of clients) {
    const dates = (c.barberDates?.[barberId] || [])
      .map((d) => Date.parse(`${d}T00:00:00`)).filter((n) => !Number.isNaN(n)).sort((a, b) => a - b);
    if (!dates.length) continue;
    const anchor = dates.find((t) => t >= matureStart && t <= matureEnd);
    if (anchor == null) continue;
    denom++;
    if (dates.some((t) => t > anchor && t <= anchor + 45 * 86400000)) yes++;
  }
  return denom >= 5 ? Math.round((yes / denom) * 1000) / 10 : null;
}

/**
 * REAL tenure for a barber = weeks since their EARLIEST client visit in the imported history. The
 * `joinDate` on the barber doc is "when added to SLIK" (often recent/wrong — e.g. the owner shows a
 * 2026 join), which mis-ramps veterans to stage 1. Their first real client date is the honest signal.
 */
export function barberTenureWeeksById(clients: ClientRecord[], barberId: string, nowMs: number): number | null {
  let min = Infinity;
  for (const c of clients) {
    for (const d of (c.barberDates?.[barberId] || [])) {
      const t = Date.parse(`${d}T00:00:00`);
      if (!Number.isNaN(t) && t < min) min = t;
    }
  }
  return min === Infinity ? null : Math.max(0, (nowMs - min) / (7 * 86400000));
}

/** Portal "have you been here before?" — find a client by phone → email → name. */
export function findClient(
  clients: ClientRecord[],
  q: { phone?: string; email?: string; name?: string },
): ClientRecord | null {
  // Normalize to the last-10-digit key on BOTH sides (Jose 2026-10-27) so a formatted stored phone
  // "(973) 879-9847" matches a bare-digit query "9738799847" (and the +1 / 1-prefix variants).
  const _key = (p?: string | null) => { const d = (p || '').replace(/\D/g, ''); return d.length > 10 ? d.slice(-10) : d; };
  const phone = _key(q.phone);
  const email = (q.email || '').trim().toLowerCase();
  const name = (q.name || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (phone) { const m = clients.find((c) => c.phone && _key(c.phone) === phone); if (m) return m; }
  if (email) { const m = clients.find((c) => c.email && c.email === email); if (m) return m; }
  if (name) { const m = clients.find((c) => c.name.toLowerCase().replace(/\s+/g, ' ') === name); if (m) return m; }
  return null;
}
